// Venue create + update + image + calendar entry schemas.
//
// Wave B refactor: slug fields (`venue_types`, `art_forms_accepted`,
// `vibe_tags`, amenity scopes) now validate structurally via `slugSchema`
// rather than closing over the seed-time const arrays from `../vocab.js`.
// The existence check (slug present + enabled in `art_forms` / etc.) happens
// in the worker route handler after zod parses, against the live KV vocab
// snapshot. This lets Richie add a new art form via /admin/settings/vocab
// without a deploy — the const arrays in `vocab.ts` stay as the seed source.
import { z } from 'zod';

import {
  countryCodeSchema,
  imageRefSchema,
  isoDateSchema,
  latSchema,
  lngSchema,
  richTextSchema,
  safeNameSchema,
  slugSchema,
  timezoneSchema,
  vocabSlugSchema,
} from './common.js';

export const createVenueInputSchema = z
  .object({
    name: safeNameSchema(2, 120),
    slug: slugSchema,
    summary: z.string().max(160).optional(),
    description: richTextSchema(4000),
    venue_types: z.array(vocabSlugSchema).min(1).max(4),
    art_forms_accepted: z.array(vocabSlugSchema).min(1).max(20),
    vibe_tags: z.array(vocabSlugSchema).max(8).optional(),
    capacity_min: z.number().int().positive(),
    capacity_max: z.number().int().positive(),
    address_line: z.string().min(2).max(200),
    city: z.string().min(1).max(80),
    region: z.string().max(80).optional(),
    country: countryCodeSchema,
    postcode: z.string().max(20).optional(),
    lat: latSchema,
    lng: lngSchema,
    address_visible_to_public: z.boolean().default(false),
    timezone: timezoneSchema,
    usual_event_times: z.string().max(280).optional(),
    sound_tech: z.array(vocabSlugSchema).optional(),
    sleeping_arrangements: z.array(vocabSlugSchema).optional(),
    food_offered: z.array(vocabSlugSchema).optional(),
    drink_offered: z.array(vocabSlugSchema).optional(),
    facilities: z.array(vocabSlugSchema).optional(),
    house_rules: z.string().max(2000).optional(),
    stipend_offered: z.boolean().default(false),
    stipend_description: z.string().max(280).optional(),
    // North Star §3 — optional extras a venue typically offers beyond
    // accommodation + meals. Boolean toggles + free-text description, mirroring
    // the stipend pattern. Per-exchange echoes live on `exchanges`.
    travel_contribution_default: z.boolean().default(false),
    travel_contribution_description: z.string().max(280).optional(),
    equipment_default: z.boolean().default(false),
    equipment_description: z.string().max(280).optional(),
    promotion_default: z.boolean().default(false),
    promotion_description: z.string().max(280).optional(),
    recording_default: z.boolean().default(false),
    recording_description: z.string().max(280).optional(),
  })
  .refine((v) => v.capacity_min <= v.capacity_max, {
    message: 'capacity_min must be ≤ capacity_max',
    path: ['capacity_max'],
  });
export type CreateVenueInput = z.infer<typeof createVenueInputSchema>;

// Update is partial-of-create plus state controls.
export const updateVenueInputSchema = z
  .object({
    name: safeNameSchema(2, 120).optional(),
    summary: z.string().max(160).optional(),
    description: richTextSchema(4000).optional(),
    venue_types: z.array(slugSchema).min(1).max(4).optional(),
    art_forms_accepted: z.array(slugSchema).min(1).max(20).optional(),
    vibe_tags: z.array(slugSchema).max(8).optional(),
    capacity_min: z.number().int().positive().optional(),
    capacity_max: z.number().int().positive().optional(),
    address_line: z.string().min(2).max(200).optional(),
    city: z.string().min(1).max(80).optional(),
    region: z.string().max(80).optional(),
    country: countryCodeSchema.optional(),
    postcode: z.string().max(20).optional(),
    lat: latSchema.optional(),
    lng: lngSchema.optional(),
    address_visible_to_public: z.boolean().optional(),
    timezone: timezoneSchema.optional(),
    usual_event_times: z.string().max(280).optional(),
    sound_tech: z.array(vocabSlugSchema).optional(),
    sleeping_arrangements: z.array(vocabSlugSchema).optional(),
    food_offered: z.array(vocabSlugSchema).optional(),
    drink_offered: z.array(vocabSlugSchema).optional(),
    facilities: z.array(vocabSlugSchema).optional(),
    house_rules: z.string().max(2000).optional(),
    stipend_offered: z.boolean().optional(),
    stipend_description: z.string().max(280).optional(),
    travel_contribution_default: z.boolean().optional(),
    travel_contribution_description: z.string().max(280).optional(),
    equipment_default: z.boolean().optional(),
    equipment_description: z.string().max(280).optional(),
    promotion_default: z.boolean().optional(),
    promotion_description: z.string().max(280).optional(),
    recording_default: z.boolean().optional(),
    recording_description: z.string().max(280).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Pass at least one field to update.' });
export type UpdateVenueInput = z.infer<typeof updateVenueInputSchema>;

export const transitionVenueStatusInputSchema = z.object({
  to: z.enum(['draft', 'pending_review', 'live', 'paused', 'removed']),
  reason: z.string().max(500).optional(),
});
export type TransitionVenueStatusInput = z.infer<typeof transitionVenueStatusInputSchema>;

export const venueImageUploadInputSchema = z.object({
  alt_text: z.string().min(1).max(280),
  caption: z.string().max(280).optional(),
  position: z.number().int().nonnegative().max(11),
  is_hero: z.boolean().default(false),
  width_px: z.number().int().positive(),
  height_px: z.number().int().positive(),
  // url is filled in by the upload service post-R2 PUT, which mints a
  // same-origin path rather than an absolute URL — hence imageRefSchema.
  url: imageRefSchema,
});
export type VenueImageUploadInput = z.infer<typeof venueImageUploadInputSchema>;

export const reorderVenueImagesInputSchema = z.object({
  ordered_image_ids: z.array(z.string().min(8).max(64)).min(1).max(12),
});
export type ReorderVenueImagesInput = z.infer<typeof reorderVenueImagesInputSchema>;

export const calendarEntryInputSchema = z
  .object({
    entry_kind: z.enum(['block', 'open_window']),
    start_date: isoDateSchema,
    end_date: isoDateSchema,
    recurrence_rule: z.string().max(280).optional(),
    notes: z.string().max(280).optional(),
  })
  .refine((v) => v.start_date <= v.end_date, {
    message: 'start_date must be ≤ end_date',
    path: ['end_date'],
  });
export type CalendarEntryInput = z.infer<typeof calendarEntryInputSchema>;
